import { useState } from "react";
import { X, Loader2, CheckCircle, Calendar } from "lucide-react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";

// ⚙️ CONFIG: Update your Calendly URL here
// Get yours at: https://calendly.com/event_types/user/me
const CALENDLY_URL = "https://calendly.com/ivanasilva/discovery-call";

const BookingModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    preferred_time: "",
    program_interest: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const programOptions = [
    "Perimenopause Decoded ($47/mo)",
    "Perimenopause Transformation ($3,300)",
    "Radiance Elite™ 1:1 ($8,500)",
    "Not sure yet — need guidance"
  ];

  const timePreferences = [
    "Morning (9am-12pm)",
    "Afternoon (12pm-5pm)",
    "Evening (5pm-8pm)",
    "Flexible"
  ];

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const openCalendly = () => {
    // Build prefilled URL if we have name/email
    const params = new URLSearchParams();
    if (formData.name) params.set("name", formData.name);
    if (formData.email) params.set("email", formData.email);
    if (formData.program_interest) {
      params.set("a1", formData.program_interest);
    }
    const url = params.toString()
      ? `${CALENDLY_URL}?${params.toString()}`
      : CALENDLY_URL;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email) {
      toast.error("Please fill in your name and email");
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/booking`, formData);
      setIsSubmitted(true);
    } catch (error) {
      console.error("Booking error:", error);
      toast.error("Unable to submit booking. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      name: "",
      email: "",
      phone: "",
      preferred_time: "",
      program_interest: "",
      message: ""
    });
    setIsSubmitted(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modal-backdrop" onClick={handleClose} data-testid="booking-modal">
      <div
        className="bg-[#F9F8F6] rounded-3xl max-w-xl w-full max-h-[90vh] overflow-y-auto animate-scale-in"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-[#F9F8F6] border-b border-[#E3DAC9] p-6 rounded-t-3xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#C5A059]/20 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-[#C5A059]" />
              </div>
              <div>
                <span className="overline text-xs">Program Inquiry</span>
                <h2 className="font-heading text-xl text-[#2C3524]">
                  Get in Touch
                </h2>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-[#E3DAC9] rounded-full transition-colors"
              data-testid="booking-close-btn"
            >
              <X className="w-5 h-5 text-[#4A5543]" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-8">
          {!isSubmitted ? (
            <form onSubmit={handleSubmit} className="space-y-5">
              <p className="text-[#4A5543] font-light mb-6">
                Share a few details and I'll be in touch to explore which
                program is the right fit for you.
              </p>

              <div>
                <label className="block text-sm text-[#4A5543] mb-2">Your Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="input-field"
                  placeholder="Enter your full name"
                  required
                  data-testid="booking-name-input"
                />
              </div>

              <div>
                <label className="block text-sm text-[#4A5543] mb-2">Email Address *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="input-field"
                  placeholder="Enter your email"
                  required
                  data-testid="booking-email-input"
                />
              </div>

              <div>
                <label className="block text-sm text-[#4A5543] mb-2">Phone Number (optional)</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="input-field"
                  placeholder="Enter your phone number"
                  data-testid="booking-phone-input"
                />
              </div>

              <div>
                <label className="block text-sm text-[#4A5543] mb-2">Program Interest</label>
                <select
                  name="program_interest"
                  value={formData.program_interest}
                  onChange={handleChange}
                  className="input-field"
                  data-testid="booking-program-select"
                >
                  <option value="">Select a program...</option>
                  {programOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm text-[#4A5543] mb-2">
                  What's your biggest challenge right now? (optional)
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  className="input-field min-h-[100px] resize-none"
                  placeholder="Share what you're experiencing..."
                  data-testid="booking-message-input"
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary w-full"
                data-testid="booking-submit-btn"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    Submitting...
                  </>
                ) : (
                  "Send My Inquiry"
                )}
              </button>
              <p className="text-xs text-center text-[#4A5543]/60">
                I'll personally reply within 48 hours
              </p>
            </form>
          ) : (
            <div className="text-center py-8 animate-scale-in">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="font-heading text-2xl text-[#2C3524] mb-3">
                Thank you, {formData.name.split(" ")[0]}
              </h3>
              <p className="text-[#4A5543] mb-6 leading-relaxed">
                Your inquiry is with me. I'll personally reply within 48 hours
                with next steps and program details.
              </p>
              <button
                onClick={handleClose}
                className="btn-primary"
                data-testid="booking-close-success-btn"
              >
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BookingModal;
